/* Shared building blocks of the list filters. */
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconAdjustmentsHorizontal,
  IconBuilding,
  IconBuildingSkyscraper,
  IconBuildingStore,
  IconBuildingWarehouse,
  IconCalendarEvent,
  IconChevronDown,
  IconCircleCheck,
  IconCircleX,
  IconToggleRight,
} from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import { twMerge } from 'tailwind-merge';
import dayjs, { Dayjs } from 'dayjs';
import { DateTimePicker } from '../DateTimePicker';
import { RichSelect, RichSelectOption } from '@/components/Controls/RichSelect';
import { NotFound } from '@/components/Message/NotFound';
import './filter-kit.css';

export type FilterTone =
  | 'violet'
  | 'sky'
  | 'indigo'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'teal'
  | 'orange'
  | 'pink'
  | 'slate';

const toneClassNames: Record<FilterTone, string> = {
  violet: 'bg-violet-50 text-violet-600',
  sky: 'bg-sky-50 text-sky-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  rose: 'bg-rose-50 text-rose-600',
  teal: 'bg-teal-50 text-teal-600',
  orange: 'bg-orange-50 text-orange-600',
  pink: 'bg-pink-50 text-pink-600',
  slate: 'bg-slate-100 text-slate-600',
};

type IconType = TablerIcon;

export const ToneIcon = ({
  icon: Icon,
  tone = 'violet',
  size = 'md',
}: {
  icon: IconType;
  tone?: FilterTone;
  size?: 'sm' | 'md';
}) => (
  <span
    className={twMerge(
      'flex shrink-0 items-center justify-center rounded-md',
      size === 'sm' ? 'h-5 w-5' : 'h-6 w-6',
      toneClassNames[tone],
    )}
  >
    <Icon size={size === 'sm' ? 13 : 15} stroke={2} />
  </span>
);

/** Labelled filter field with a colored pictogram. */
export const FilterField = ({
  icon,
  tone,
  label,
  children,
  className,
}: {
  icon: IconType;
  tone?: FilterTone;
  label: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={twMerge('flex min-w-0 flex-col gap-1.5', className)}>
    <div className="flex items-center gap-2 text-2sm font-medium text-gray-700 dark-mode:text-gray-700">
      <ToneIcon icon={icon} tone={tone} size="sm" />
      {label}
    </div>
    {children}
  </div>
);

/** On/off filter displayed as a switch inside a small card. */
export const FilterSwitch = ({
  icon = IconToggleRight,
  tone = 'violet',
  label,
  checked,
  onChange,
  className,
}: {
  icon?: IconType;
  tone?: FilterTone;
  label: string;
  checked?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}) => (
  <label
    className={twMerge(
      'flex min-h-[36px] cursor-pointer select-none items-center gap-2.5 self-end rounded-md py-1 border border-gray-300 bg-gray-FCFCFC px-2.5 text-2sm text-gray-700 transition-colors hover:border-gray-400',
      checked && 'border-purple-5433FF/40 bg-purple-F7F5FF/60',
      'dark-mode:bg-coal-500 dark-mode:border-gray-300 dark-mode:text-gray-700',
      className,
    )}
  >
    <ToneIcon icon={icon} tone={tone} size="sm" />
    <span className="flex-1 leading-tight">{label}</span>
    <span className="switch switch-sm">
      <input
        type="checkbox"
        checked={Boolean(checked)}
        className={twMerge(checked && 'bg-primary')}
        onChange={onChange}
      />
    </span>
  </label>
);

/** Date range ("between ... and ..."). */
export const FilterDateRange = ({
  label,
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  tone = 'amber',
  className,
}: {
  label: string;
  startDate?: string;
  endDate?: string;
  onStartChange?: (value: Dayjs) => void;
  onEndChange?: (value: Dayjs) => void;
  tone?: FilterTone;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <FilterField
      icon={IconCalendarEvent}
      tone={tone}
      label={label}
      className={twMerge(
        'select-datepicker-container filter-date-range',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <DateTimePicker
            useTime={false}
            value={startDate ? dayjs(startDate) : null}
            onChange={onStartChange}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            maxDate={endDate ? dayjs(endDate) : null}
            textFieldSx={{ width: '100%', maxWidth: 'none' }}
          />
        </div>
        <span className="shrink-0 text-xs text-gray-500">
          {t('common:and').toLowerCase()}
        </span>
        <div className="min-w-0 flex-1">
          <DateTimePicker
            useTime={false}
            value={endDate ? dayjs(endDate) : null}
            onChange={onEndChange}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            minDate={startDate ? dayjs(startDate) : null}
            textFieldSx={{ width: '100%', maxWidth: 'none' }}
          />
        </div>
      </div>
    </FilterField>
  );
};

export enum FilterEntity {
  DISTRIBUTOR = 'DISTRIBUTOR',
  GROUP = 'GROUP',
  COMPANY = 'COMPANY',
  BRANCH = 'BRANCH',
}

export const FILTER_ENTITY_CONFIG: Record<
  FilterEntity,
  { icon: IconType; tone: FilterTone; labelKey: string; placeholderKey: string }
> = {
  [FilterEntity.DISTRIBUTOR]: {
    icon: IconBuildingStore,
    tone: 'sky',
    labelKey: 'common:distributor',
    placeholderKey: 'common:allDistributors',
  },
  [FilterEntity.GROUP]: {
    icon: IconBuildingSkyscraper,
    tone: 'violet',
    labelKey: 'common:group',
    placeholderKey: 'common:allGroups',
  },
  [FilterEntity.COMPANY]: {
    icon: IconBuilding,
    tone: 'indigo',
    labelKey: 'common:company',
    placeholderKey: 'common:allCompanies',
  },
  [FilterEntity.BRANCH]: {
    icon: IconBuildingWarehouse,
    tone: 'emerald',
    labelKey: 'common:branch',
    placeholderKey: 'common:allBranches',
  },
};

/**
 * Autocomplete select of an entity of the structure (distributor, group,
 * company, branch). When there is no option, `emptyMessage` is displayed
 * instead of the select.
 */
export const EntityFilterSelect = ({
  entity,
  label,
  options,
  value,
  loading,
  emptyMessage,
  onChange,
  className,
}: {
  entity: FilterEntity;
  label?: string;
  options?: RichSelectOption[];
  value?: string;
  loading?: boolean;
  /** Message displayed when the list is empty (no select displayed then). */
  emptyMessage?: string;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  const config = FILTER_ENTITY_CONFIG[entity];

  return (
    <FilterField
      icon={config.icon}
      tone={config.tone}
      label={label ?? t(config.labelKey)}
      className={className}
    >
      {!loading && emptyMessage && (options?.length ?? 0) === 0 ? (
        <div className="flex h-[36px] items-center">
          <NotFound
            containerClassName="justify-start gap-1.5"
            iconClassName="-mt-1 text-[16px]"
            textClassName="text-xs"
            text={emptyMessage}
          />
        </div>
      ) : (
        <RichSelect
          searchable
          loading={loading}
          placeholder={t(config.placeholderKey)}
          options={options}
          value={value || ''}
          onChange={onChange}
        />
      )}
    </FilterField>
  );
};

const STATUS_PILL = {
  active: 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
  inactive: 'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
};

/** Options "active" / "inactive" rendered as green / red pills. */
export const getStatusPillOptions = (
  activeLabel: string,
  inactiveLabel: string,
): RichSelectOption[] => [
  {
    label: activeLabel,
    value: 'true',
    pillClassName: STATUS_PILL.active,
    icon: <IconCircleCheck size={12} stroke={2.4} />,
  },
  {
    label: inactiveLabel,
    value: 'false',
    pillClassName: STATUS_PILL.inactive,
    icon: <IconCircleX size={12} stroke={2.4} />,
  },
];

/** Removes the "all" option (value '') of option lists: RichSelect adds it from its placeholder. */
export const withoutEmptyOption = <T extends { value: string | number }>(
  options: T[],
) => options.filter((option) => option.value !== '' && option.value !== '0');

/** "More filters" button: shows / hides the advanced filters panel below the toolbar. */
export const AdvancedFilterToggle = ({
  open,
  activeCount = 0,
  onToggle,
  className,
}: {
  open: boolean;
  activeCount?: number;
  onToggle: () => void;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      aria-expanded={open}
      className={twMerge(
        'inline-flex h-[36px] items-center gap-2 whitespace-nowrap rounded-md border px-3 text-2sm font-medium transition-colors',
        open
          ? 'border-purple-5433FF bg-purple-5433FF text-white shadow-md'
          : 'border-gray-300 bg-white text-black-252E49 hover:border-purple-5433FF/50 hover:text-purple-5433FF dark-mode:bg-coal-500 dark-mode:text-gray-700',
        className,
      )}
      onClick={onToggle}
    >
      <IconAdjustmentsHorizontal size={16} stroke={2} />
      {t('common:moreFilters')}
      {activeCount > 0 && (
        <span
          className={twMerge(
            'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-semibold',
            open
              ? 'bg-white text-purple-5433FF'
              : 'bg-purple-5433FF text-white',
          )}
        >
          {activeCount}
        </span>
      )}
      <IconChevronDown
        size={15}
        className={twMerge('transition-transform', open && 'rotate-180')}
      />
    </button>
  );
};

/** Area displayed below the toolbar (not in an overlay) with the advanced filters. */
export const AdvancedFilterPanel = ({
  open,
  children,
  className,
}: {
  open: boolean;
  children: React.ReactNode;
  className?: string;
}) =>
  open ? (
    <div
      className={twMerge(
        'rounded-xl border border-purple-5433FF/15 bg-gradient-to-br from-purple-F7F5FF/70 to-white p-4 dark-mode:from-coal-500 dark-mode:to-coal-600 dark-mode:border-gray-300',
        className,
      )}
    >
      {children}
    </div>
  ) : null;

/** Responsive grid used inside the advanced filters panel. */
export const FilterGrid = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={twMerge(
      'grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2 xl:grid-cols-4',
      className,
    )}
  >
    {children}
  </div>
);
